import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { STATUS_BADGE, PO_STATUS_BADGE, formatCurrency, formatNumber } from "@/lib/constants";

/* -------- Month-over-Month momentum strip (department headers) -------- */
export function MomStrip({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3" data-testid="mom-strip">
      {items.map((m) => {
        const fmt = m.is_currency ? formatCurrency : formatNumber;
        const flat = m.delta_pct === 0;
        const up = m.delta_pct > 0;
        const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
        const tone = flat ? "text-slate-400" : up ? "text-emerald-600" : "text-red-600";
        return (
          <div
            key={m.label}
            className="flex items-center gap-3 border border-slate-200 rounded-lg px-3.5 py-2 bg-white"
            data-testid={`mom-${m.label.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold">{m.label}</div>
              <div className="text-lg font-bold text-slate-900 font-[Manrope] leading-tight">{fmt(m.this_month)}</div>
              <div className="text-[10px] text-slate-400">last mo · {fmt(m.last_month)}</div>
            </div>
            <div className={`flex items-center gap-0.5 text-xs font-semibold ${tone}`}>
              <Icon className="h-3.5 w-3.5" />
              {Math.abs(m.delta_pct)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------- Small building blocks shared by all department dashboards -------- */

export function KpiTile({ label, value, sub, icon: Icon, accent = "slate", testid }) {
  const ACCENT = {
    slate: "bg-slate-50 text-slate-600",
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    violet: "bg-violet-50 text-violet-600",
  }[accent];
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-bold text-slate-900 mt-1.5 font-[Manrope]">{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${ACCENT}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TableShell({ cols, rows, empty, renderRow, testid }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{c}</th>
            ))}
            <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
          </tr>
        </thead>
        <tbody data-testid={testid}>
          {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center py-10 text-slate-500">{empty}</td></tr>}
          {rows.map(renderRow)}
        </tbody>
      </table>
    </div>
  );
}

function OpenLink({ to, testid }) {
  return (
    <Link to={to} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={testid}>
      Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
    </Link>
  );
}

export function ActivePoTable({ rows }) {
  return (
    <TableShell
      testid="active-po-table"
      cols={["PO No.", "Buyer", "Product", "Qty", "Value", "Delivery", "Status", "Payment"]}
      rows={rows}
      empty="No active POs."
      renderRow={(p) => (
        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`po-row-${p.id}`}>
          <td className="py-2.5 px-3 font-mono font-semibold">{p.po_number || "—"}</td>
          <td className="py-2.5 px-3">{p.buyer || "—"}</td>
          <td className="py-2.5 px-3 text-slate-700">{p.product || "—"}</td>
          <td className="py-2.5 px-3 font-mono">{formatNumber(p.quantity)}</td>
          <td className="py-2.5 px-3 font-mono font-semibold">{formatCurrency(p.contract_value)}</td>
          <td className="py-2.5 px-3 text-slate-700">{p.delivery_date || "—"}</td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={PO_STATUS_BADGE(p.status || "")}>{p.status || "—"}</Badge></td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(p.payment_status || "")}>{p.payment_status || "—"}</Badge></td>
          <td className="py-2.5 px-3 text-right"><OpenLink to={`/m/purchase_orders/${p.id}`} testid={`po-open-${p.id}`} /></td>
        </tr>
      )}
    />
  );
}

export function DispatchTable({ rows }) {
  return (
    <TableShell
      testid="dispatch-table"
      cols={["PO No.", "Product", "Total", "Dispatched", "Pending", "Invoice", "Transport", "Delivery", "Payment"]}
      rows={rows}
      empty="No dispatch records."
      renderRow={(d) => (
        <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`dispatch-row-${d.id}`}>
          <td className="py-2.5 px-3 font-mono font-semibold">{d.po_number || "—"}</td>
          <td className="py-2.5 px-3">{d.product || "—"}</td>
          <td className="py-2.5 px-3 font-mono">{formatNumber(d.total_qty)}</td>
          <td className="py-2.5 px-3 font-mono">{formatNumber(d.dispatched_qty)}</td>
          <td className="py-2.5 px-3 font-mono text-amber-700">{formatNumber(d.pending_qty)}</td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(d.invoice_status || "")}>{d.invoice_status || "—"}</Badge></td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(d.transport_status || "")}>{d.transport_status || "—"}</Badge></td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(d.delivery_status || "")}>{d.delivery_status || "—"}</Badge></td>
          <td className="py-2.5 px-3">{d.payment_pending === "Yes"
            ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Pending</Badge>
            : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">OK</Badge>}</td>
          <td className="py-2.5 px-3 text-right"><OpenLink to={`/m/dispatch/${d.id}`} testid={`dispatch-open-${d.id}`} /></td>
        </tr>
      )}
    />
  );
}

function dueDateClass(t) {
  if (t.overdue) return "text-red-600 font-semibold";
  if (t.due_soon) return "text-amber-700";
  return "text-slate-700";
}

export function TasksTable({ rows }) {
  return (
    <TableShell
      testid="tasks-table"
      cols={["Task", "Assigned To", "Assigned By", "Priority", "Due", "Status"]}
      rows={rows}
      empty="All caught up — no pending tasks."
      renderRow={(t) => (
        <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${t.overdue ? "bg-red-50/40" : ""}`} data-testid={`task-row-${t.id}`}>
          <td className="py-2.5 px-3 font-semibold text-slate-900">{t.task_title || "—"}</td>
          <td className="py-2.5 px-3 text-slate-700">{t.assigned_to || "—"}</td>
          <td className="py-2.5 px-3 text-slate-600">{t.assigned_by || "—"}</td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.priority || "")}>{t.priority || "—"}</Badge></td>
          <td className="py-2.5 px-3"><span className={dueDateClass(t)}>{t.due_date || "—"}{t.overdue && " · OVERDUE"}</span></td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.status)}>{t.status}</Badge></td>
          <td className="py-2.5 px-3 text-right"><OpenLink to={`/m/tasks/${t.id}`} testid={`task-open-${t.id}`} /></td>
        </tr>
      )}
    />
  );
}

export function StockTable({ rows }) {
  return (
    <TableShell
      testid="stock-table"
      cols={["Product", "Model", "Category", "Current", "Dispatch Pending", "Production Pending", "Available", "Status"]}
      rows={rows}
      empty="No products in catalogue."
      renderRow={(p) => (
        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`stock-row-${p.id}`}>
          <td className="py-2.5 px-3 font-semibold text-slate-900">{p.product_name || "—"}</td>
          <td className="py-2.5 px-3 font-mono">{p.model_no || "—"}</td>
          <td className="py-2.5 px-3 text-slate-600">{p.category || "—"}</td>
          <td className="py-2.5 px-3 font-mono">{formatNumber(p.current_stock)}</td>
          <td className="py-2.5 px-3 font-mono text-amber-700">{formatNumber(p.dispatch_pending)}</td>
          <td className="py-2.5 px-3 font-mono">{formatNumber(p.production_pending)}</td>
          <td className={`py-2.5 px-3 font-mono font-bold ${p.available > 0 ? "text-emerald-700" : "text-red-600"}`}>{formatNumber(p.available)}</td>
          <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(p.status || "")}>{p.status || "—"}</Badge></td>
          <td className="py-2.5 px-3 text-right"><OpenLink to={`/m/products/${p.id}`} testid={`stock-open-${p.id}`} /></td>
        </tr>
      )}
    />
  );
}

export function DashHeader({ eyebrow, title, hint, right }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{eyebrow}</div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{title}</h1>
        {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
      </div>
      {right}
    </div>
  );
}
